import { useCallback, useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useGetEmailTemplatesQuery } from '@/services/endpoints/campaigns';
import { WhatsappTemplateCategoryApi, WhatsappTemplateResponse, WhatsappTemplateStatusApi } from '@/services/endpoints/campaigns/types';
import { EmailTemplate, EmailTemplateCategory, EmailTemplateInput, EmailTemplateStatus } from '../types/emailTemplates';
import { sanitizeTemplateHtml } from '@/features/campaigns/v2/utils/templateHtml';

const USE_EMAIL_TEMPLATES_API = true;

const wait = (delayMs = 160) => new Promise((resolve) => setTimeout(resolve, delayMs));

const nowAsLabel = () => {
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date());

  return `Updated ${formatted}`;
};

const STATUS_MAP: Record<string, EmailTemplateStatus> = {
  APPROVED: 'approved',
  IN_REVIEW: 'inReview',
  REJECTED: 'rejected',
  DRAFT: 'draft',
};

const CATEGORY_MAP: Record<string, EmailTemplateCategory> = {
  MARKETING: 'marketing',
  UTILITY: 'utility',
  AUTHENTICATION: 'authentication',
};

const mapStatus = (status?: WhatsappTemplateStatusApi): EmailTemplateStatus => {
  if (!status) {
    return 'draft';
  }

  return STATUS_MAP[status] ?? 'draft';
};

const mapCategory = (category?: WhatsappTemplateCategoryApi): EmailTemplateCategory => {
  if (!category) {
    return 'marketing';
  }

  return CATEGORY_MAP[category] ?? 'marketing';
};

const formatUpdatedLabel = (rawDate?: string) => {
  if (!rawDate) {
    return 'N/A';
  }

  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  return `Updated ${new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date)}`;
};

const mapTemplateResponseToUi = (item: WhatsappTemplateResponse): EmailTemplate => ({
  id: item.id || `template_${Date.now()}`,
  name: item.name || 'N/A',
  body: sanitizeTemplateHtml(item.body || 'N/A'),
  language: item.language || 'N/A',
  category: mapCategory(item.category),
  status: mapStatus(item.status),
  updatedAt: formatUpdatedLabel(item.updatedOn || item.lastModified),
});

const createTemplate = async (input: EmailTemplateInput) => {
  await wait();
  const next: EmailTemplate = {
    id: `${input.name.trim().toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`,
    name: input.name.trim(),
    body: sanitizeTemplateHtml(input.body),
    language: 'Italian · v1',
    category: 'marketing',
    status: 'draft',
    updatedAt: nowAsLabel(),
  };

  return next;
};

const updateTemplate = async (templateId: string, input: EmailTemplateInput): Promise<EmailTemplate> => {
  await wait();
  return {
    id: templateId,
    name: input.name.trim() || 'N/A',
    body: sanitizeTemplateHtml(input.body),
    language: 'N/A',
    category: 'marketing',
    status: 'draft',
    updatedAt: nowAsLabel(),
  };
};

export const useEmailTemplates = () => {
  const workspace = useWorkspace();
  const { data, isFetching, refetch } = useGetEmailTemplatesQuery({ workspaceId: workspace.id }, { skip: !workspace.id });
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const normalizedTemplates = useMemo(() => {
    if (!Array.isArray(data)) {
      return [];
    }
    const first = data[0] as unknown;
    const nested =
      first && typeof first === 'object' && 'templates' in first && Array.isArray((first as { templates: WhatsappTemplateResponse[] }).templates)
        ? (first as { templates: WhatsappTemplateResponse[] }).templates
        : undefined;
    const templatesFromApi = nested ?? (data as WhatsappTemplateResponse[]);
    return templatesFromApi.map(mapTemplateResponseToUi);
  }, [data]);

  useEffect(() => {
    setTemplates(normalizedTemplates);
  }, [normalizedTemplates]);

  const reload = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const create = useCallback(
    async (input: EmailTemplateInput) => {
      const created = await createTemplate(input);
      setTemplates((prev) => [created, ...prev]);
      return created;
    },
    [setTemplates]
  );

  const update = useCallback(
    async (templateId: string, input: EmailTemplateInput) => {
      const updated = await updateTemplate(templateId, input);

      if (!updated) {
        return undefined;
      }

      setTemplates((prev) => prev.map((item) => (item.id === templateId ? updated : item)));
      return updated;
    },
    [setTemplates]
  );

  const getById = useCallback(async (templateId: string) => templates.find((item) => item.id === templateId), [templates]);

  return {
    templates,
    templatesCount: templates.length,
    isLoading: isFetching,
    reload,
    create,
    update,
    getById,
    isUsingApi: USE_EMAIL_TEMPLATES_API,
  };
};
